import { t } from 'i18next';
import { Flow, GoodsToPersonsExtension, UsageProfile } from '@thive/fddm-types';

import { InvalidFlow } from './types';
import {
  MAX_END_WEIGHT,
  MAX_RATE,
  MAX_START_WEIGHT,
  MIN_END_WEIGHT,
  MIN_RATE,
  MIN_START_WEIGHT,
} from '../consts';
import { UsageProfileOptions } from '../types';
import { isOutOfRange } from '../helpers';

function validateUsageProfile(
  flow: Flow,
  profile: UsageProfile,
  profileName: UsageProfileOptions,
): InvalidFlow[] {
  const invalidFlows: InvalidFlow[] = [];

  const base = {
    areaId: '?',
    areaName: flow.name ?? '?',
    flowId: flow.id,
  };

  if (profile.type === 'LINEAR') {
    if (!profile.usageProfileLinear) return invalidFlows;

    const { startWeight, endWeight } = profile.usageProfileLinear;

    if (isOutOfRange(startWeight, MIN_START_WEIGHT, MAX_START_WEIGHT)) {
      invalidFlows.push({
        ...base,
        reason: 'out_of_range',
        context: {
          profile: t(`interface:properties.flows.goods_to_persons.usage_profiles.${profileName}`),
          field: 'start weight',
          minValue: MIN_START_WEIGHT,
          maxValue: MAX_START_WEIGHT,
        },
      });
    }

    if (isOutOfRange(endWeight, MIN_END_WEIGHT, MAX_END_WEIGHT)) {
      invalidFlows.push({
        ...base,
        reason: 'out_of_range',
        context: {
          profile: t(`interface:properties.flows.goods_to_persons.usage_profiles.${profileName}`),
          field: 'end weight',
          minValue: MIN_END_WEIGHT,
          maxValue: MAX_END_WEIGHT,
        },
      });
    }
  } else if (profile.type === 'EXPONENTIAL') {
    if (!profile.usageProfileExponential) return invalidFlows;
    const { rate } = profile.usageProfileExponential;

    if (isOutOfRange(rate, MIN_RATE, MAX_RATE)) {
      invalidFlows.push({
        ...base,
        reason: 'out_of_range',
        context: {
          profile: t(`interface:properties.flows.goods_to_persons.usage_profiles.${profileName}`),
          field: 'rate',
          minValue: MIN_RATE,
          maxValue: MAX_RATE,
        },
      });
    }
  }

  return invalidFlows;
}

function validateUsageProfiles(flows: Flow[]): InvalidFlow[] {
  const invalids: InvalidFlow[] = [];

  flows.forEach((flow) => {
    const ext: GoodsToPersonsExtension | undefined = flow.goodsToPersonsExtension;
    if (!ext) return;

    const { aisleProfile, shelfProfile, depthProfile, processAreaUsage } = ext;

    if (aisleProfile) invalids.push(...validateUsageProfile(flow, aisleProfile, 'aisleProfile'));
    if (shelfProfile) invalids.push(...validateUsageProfile(flow, shelfProfile, 'shelfProfile'));
    if (depthProfile) invalids.push(...validateUsageProfile(flow, depthProfile, 'depthProfile'));
    invalids.push(...validateUsageProfile(flow, processAreaUsage, 'processAreaUsage'));
  });

  return invalids;
}

export const validateGoodsToPersonFlows = (flows: Flow[]): InvalidFlow[] => [
  ...validateUsageProfiles(flows),
];
